import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { apiUrl } from "@/lib/api-client";
import { type GpsUploadSource } from "@/lib/gallery-upload-gps";
import { uploadDealFolderPhotosConcurrent } from "@/lib/deal-folder-photos-concurrent-upload";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiPhotoCapture } from "@/components/images/MultiPhotoCapture";

/** Munkaátadás: „Munkáink” fotók a deal képmappájába (`realizacio` mappa, ha új). */
export function HandoverPhotoStep({
  dealId,
  onPhotosChanged,
  onUploadBusyChange,
  onFolderIdChange,
}: {
  dealId: string;
  onPhotosChanged?: () => void;
  onUploadBusyChange?: (busy: boolean) => void;
  onFolderIdChange?: (folderId: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [uploadBusy, setUploadBusy] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>();

  useEffect(() => {
    onUploadBusyChange?.(uploadBusy);
  }, [uploadBusy, onUploadBusyChange]);

  const foldersQuery = useQuery({
    queryKey: ["image-folders", { dealId }],
    queryFn: async () => {
      if (!dealId) return [];
      const r = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const j = await r.json();
      return j.folders as any[];
    },
    enabled: !!dealId,
  });

  const folderIdFromList = foldersQuery.data?.[0]?.id as string | undefined;

  useEffect(() => {
    if (folderIdFromList) setActiveFolderId(folderIdFromList);
  }, [folderIdFromList]);

  const folderId = activeFolderId ?? folderIdFromList;

  useEffect(() => {
    onFolderIdChange?.(folderId);
  }, [folderId, onFolderIdChange]);

  const photosQuery = useQuery({
    queryKey: ["image-folder-photos", folderId, "munkaink", "handover"],
    queryFn: async () => {
      if (!folderId) return [];
      const params = new URLSearchParams({ subfolderType: "munkaink" });
      const r = await fetch(
        apiUrl(`/api/images/folders/${folderId}/photos?${params.toString()}`),
        { credentials: "include" }
      );
      if (!r.ok) return [];
      const j = await r.json();
      return j.photos as any[];
    },
    enabled: !!folderId,
  });

  const photos = photosQuery.data || [];
  const hasPhotos = photos.length > 0;

  const handleUpload = async (files: File[], sources: GpsUploadSource[]) => {
    if (!files.length || !dealId) return;
    setUploadBusy(true);
    try {
      let targetFolderId = folderId;
      if (!targetFolderId) {
        let dealTitle = "";
        try {
          const dealRes = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
          if (dealRes.ok) {
            const dealData = await dealRes.json();
            const d = dealData.deal ?? dealData;
            dealTitle = (d.title || d.name || "").trim() || "Deal";
          }
        } catch {
          dealTitle = "Deal";
        }
        const today = new Date();
        const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
        const folderName = `${dateStr} ${dealTitle}`;

        const r = await fetch(apiUrl("/api/images/folders"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: folderName,
            dealId,
            category: "realizacio",
          }),
        });
        if (!r.ok) {
          const error = await r.json().catch(() => ({}));
          throw new Error(error?.userMessage || t("measurement.createFolderFailed"));
        }
        const j = await r.json();
        targetFolderId = j.folder?.id;
        if (targetFolderId) {
          setActiveFolderId(targetFolderId);
          await qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
        }
      }

      if (!targetFolderId) {
        throw new Error(t("measurement.createPhotoFolderFailed"));
      }

      const uploadUrl = apiUrl(`/api/images/folders/${targetFolderId}/photos`);
      const photosQueryKey = ["image-folder-photos", targetFolderId, "munkaink", "handover"] as const;

      const onEachSuccess = () => {
        qc.invalidateQueries({ queryKey: [...photosQueryKey] });
        onPhotosChanged?.();
      };

      const { anySuccess, queued } = await uploadDealFolderPhotosConcurrent({
        uploadUrl,
        subfolderType: "munkaink",
        files,
        sources,
        context: { folderId: targetFolderId, dealId },
        onEachSuccess,
      });

      if (anySuccess) {
        qc.invalidateQueries({ queryKey: [...photosQueryKey] });
      }
      if (queued > 0 && !anySuccess) {
        notify.error(
          t("uploadOutbox.queuedAllFailed", { count: queued }),
          t("uploadOutbox.queuedDealPhotosDesc"),
        );
      } else if (queued > 0) {
        notify.info(
          t("uploadOutbox.partialSuccess", { queued, ok: files.length - queued }),
          t("uploadOutbox.queuedDealPhotosDesc"),
        );
      }
      onPhotosChanged?.();

      if (files.length >= 2 && (anySuccess || queued > 0)) {
        notify.info(
          t("images.uploadBackgroundStartedTitle"),
          t("images.uploadBackgroundStartedDesc", { count: files.length }),
        );
      }
    } catch (error) {
      notify.error(error instanceof Error ? error.message : "Feltöltés hiba.");
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <Card className={cn(hasPhotos ? "border-green-500" : "border-yellow-500")}>
        <CardHeader>
          <div className="flex items-center gap-2">
            {hasPhotos ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            )}
            <CardTitle>{t("handover.photosCardTitle")}</CardTitle>
          </div>
          <CardDescription>
            {hasPhotos
              ? t("handover.photosCardDescHas", { count: photos.length })
              : t("handover.photosCardDescEmpty")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasPhotos && (
            <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-800 dark:text-yellow-200">
              {t("handover.photosHintMin")}
            </div>
          )}

          <MultiPhotoCapture
            onUpload={handleUpload}
            disabled={!dealId}
            uploadBusy={uploadBusy}
            releasePendingImmediately
            cameraLabel={t("images.takePhotos")}
            chooseFilesLabel={t("images.chooseFromFiles")}
            addPhotoLabel={t("images.addPhoto")}
            uploadLabel={t("images.uploadAll")}
            uploadingLabel={t("images.uploading")}
          />

          {hasPhotos && (
            <div className="grid grid-cols-3 gap-2 mt-4">
              {photos.slice(0, 6).map((photo: any) => (
                <div
                  key={photo.id}
                  className="aspect-square rounded-lg overflow-hidden border bg-muted"
                >
                  <img
                    src={photo.thumbnail_url || photo.file_url}
                    alt={photo.file_name || "Fénykép"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {photos.length > 6 && (
                <div className="aspect-square rounded-lg border bg-muted flex items-center justify-center text-sm text-muted-foreground">
                  +{photos.length - 6}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
